import React, { useEffect, useState } from 'react';
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Card from "@amzn/meridian/card";
import Toggle from "@amzn/meridian/toggle";
import Button from "@amzn/meridian/button";
import { LMFSAccessor } from "@amzn/dolphin-web-framework";
import { getConsentStatus, updateConsentStatus } from "../insight/summary/NetworkUtils";
import { Loader, HeaderView, CycleSelector } from "@amzn/dolphin-uiweb-framework";
import { CONSENT_STATUS, EXPERIMENT_STATUS, MY_INSIGHTS_FEATURES} from "../insight/summary/Constants";
import { ANALYTICS_DESCRIPTION } from "../../constants/AnalyticsConstants";
import { AnalyticEventKeys, MobileAnalyticsHelper, DataHandler } from "@amzn/dolphin-web-framework";
import comingSoon from "../../images/coming_soon.png";

const ConsentToggle = ({ consentOption, label, status, setConsentStatus }) => {
    const [checked, setChecked] = useState(false);
    const [showConsentPrompt, setShowConsentPrompt] = useState(false);

    useEffect(() => {
        if (status === CONSENT_STATUS.NOT_FOUND) {
            setShowConsentPrompt(true);
        } else {
            setChecked(status === CONSENT_STATUS.CONSENTED);
        }    
    }, [status]);

    const handleToggleChange = (newChecked) => {
        sendConsentAnalytics(consentOption, newChecked ? CONSENT_STATUS.CONSENTED : CONSENT_STATUS.DECLINED);
        updateConsentStatus(consentOption, newChecked, (updatedStatus) => {
            if (updatedStatus) {
                setConsentStatus(updatedStatus);
                setChecked(updatedStatus === CONSENT_STATUS.CONSENTED);
            } else {
                console.error(`Failed to update consent status for ${consentOption}`);
            }
        });
    };

    const handleAccept = () => {
        handleToggleChange(true);
        setShowConsentPrompt(false);
    };

    const handleDeny = () => {
        handleToggleChange(false);
        setShowConsentPrompt(false);
    };

    if (showConsentPrompt) {
        return (
            <Card width={"100%"} spacingInset={"medium"} spacingBottom={"medium"}>
                <Column>
                    <Text type="b300">{`Enable ${label}?`}</Text>
                    <Row spacingTop={"small"} >
                        <Button onClick={handleAccept}>Accept</Button>
                        <Button onClick={handleDeny} type={"secondary"}>Deny</Button>
                    </Row>
                </Column>
            </Card>
        );
    }

    return (
        <Card width={"100%"} spacingInset={"medium"} spacingBottom={"medium"}>
            <Row alignmentVertical={"center"}>
                <Text type="b300" style={{ flex: 1 }}>{label}</Text>
                <Toggle checked={checked} onChange={handleToggleChange} />
            </Row>
            <Text type="b200" color="secondary" spacingTop={"small"}>
                Current status: {checked ? "Enabled" : "Disabled"}
            </Text>
        </Card>
    );
};

const sendConsentAnalytics = (consentOption, response) => {
    const feature = consentOption === LMFSAccessor.CONSENT_TYPE.STOW_PROCESS ?
        MY_INSIGHTS_FEATURES.STOW_PROCESS :
        MY_INSIGHTS_FEATURES.STOW_SUMMARY;

    MobileAnalyticsHelper.processAnalytics(
        Date.now(),
        ANALYTICS_DESCRIPTION.MY_INSIGHTS_SETTINGS_PAGE,
        null,
        AnalyticEventKeys.Modules.MY_INSIGHT,
        JSON.stringify({
            feature: feature,
            response: response
        }),
        DataHandler.getUserEmployeeId(),
        AnalyticEventKeys.Events.USER_UPDATED_CONSENT,
        false,
        null,
        null
    );
};

const Settings = () => {
    const [stowSummaryConsentStatus, setStowSummaryConsentStatus] = useState(null);
    const [stowProcessConsentStatus, setStowProcessConsentStatus] = useState(null);
    const [experimentStatus, setExperimentStatus] = useState(null);

    useEffect(() => {
        sendPageViewAnalytics();
        getConsentStatus(LMFSAccessor.CONSENT_TYPE.STOW_PROCESS, (status) => {
            setStowProcessConsentStatus(status);
        }, expStatus => {
            setExperimentStatus(expStatus)
        });
    }, []);

    useEffect(() => {
        getConsentStatus(LMFSAccessor.CONSENT_TYPE.STOW_SUMMARY, (status) => {
            setStowSummaryConsentStatus(status);
        });
    }, []);    

    // Display nothing until consent status is not fetched.
    if (!stowSummaryConsentStatus && !stowProcessConsentStatus) {
        return <Loader/>;
    }

    if (experimentStatus === EXPERIMENT_STATUS.CONTROL) {
        return (
            <Column spacingInset="200">
                <HeaderView
                    primaryTitle={{title:"Coming soon", size: "Large", type: "Bold"}}
                    secondaryTitle={{title: "Insights on your performance", size:"Medium", type:"simple"}}
                />
                <Row spacingInset="medium" width='100%' alignmentHorizontal="center">
                    <img alt={"coming soon"} src={comingSoon} width={"80%"}/>
                </Row>
            </Column>
        );
    }

    return (
        <Column width={"100%"} height={"100%"} spacingInset={"medium"} alignmentVertical={"start"}>
            <Text type="b300" spacingBottom={"medium"}>
                In order to view MyInsights data, please make sure the feature is enabled.
            </Text>
            <ConsentToggle consentOption={LMFSAccessor.CONSENT_TYPE.STOW_PROCESS} label="Stow Process Widget" status={stowProcessConsentStatus} setConsentStatus={setStowProcessConsentStatus}/>
            <ConsentToggle consentOption={LMFSAccessor.CONSENT_TYPE.STOW_SUMMARY} label="Stow Summary View" status={stowSummaryConsentStatus} setConsentStatus={setStowSummaryConsentStatus}/>
            <Card width={"100%"} spacingInset={"medium"} spacingBottom={"medium"}>
                <Text type="b300" spacingBottom={"small"}>Select Default Cycle</Text>
                <CycleSelector />
            </Card>
        </Column>
    );
};

const sendPageViewAnalytics = () => {
    MobileAnalyticsHelper.processAnalytics(Date.now(), ANALYTICS_DESCRIPTION.MY_INSIGHTS_SETTINGS_PAGE, null,
        AnalyticEventKeys.Modules.MY_INSIGHT, null, DataHandler.getUserEmployeeId(), AnalyticEventKeys.Events.USER_VIEWED_MY_INSIGHTS_SETTINGS_PAGE,
        false, null, null
    );
};

export default Settings;
